import { DatePicker, Select, Button } from "@veneer/core";
import React, { useEffect, useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import moment from "moment";
import { toast } from "react-toastify";
import "./edit-customer-availability.css";
import { HelpModalPopup } from "@components/home-page/shared-components/modal-popup/help-modal-popup";
import { POST_VALIDATE_DATE } from "services";
import { apiPost } from "@services/service";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch } from "@store/hooks";
import { setLoading } from "@store/reducers/loadingSlice";
import { Trans, useTranslation } from "react-i18next";

interface Props {
  data: any;
  cancelCaseHandler: () => void;
  submitAvailabilityHandler: (val: any) => void;
}
interface Option {
  label: string;
  value: string;
}
export const deepParse = (value: any) => {
  try {
    let parsed = value;
    while (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }
    return parsed;
  } catch {
    return null;
  }
};

const EditCustomerAvailability: React.FC<Props> = ({
  data,
  cancelCaseHandler,
  submitAvailabilityHandler,
}) => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [contactMethod, setContactMethod] = useState<Option | null>(null);
  const [bestTime, setBestTime] = useState<Option | null>(null);
  const [caseExtensionId, setCaseExtenasionId] = useState<any>();
  const [date, setDate] = useState<moment.Moment | null>(null);
  const [open, setOpen] = useState(false);
  const [popupTitle, setPopupTitle] = useState<string>("");
  const [dateError, setDateError] = useState<string | null>(null); // State for date error message
  const [id, setId] = useState<any>(null);
  const [initialValues, setInitialValues] = useState<any>(null);
  useEffect(() => {
    const avail = deepParse(data?.CustomerAvailability);
    const hpi_caseextensionid = String(avail.hpi_caseextensionid);
    console.log(hpi_caseextensionid, "456");
    setCaseExtenasionId(hpi_caseextensionid);
  }, [data]);

  const preferredContactOptions = useMemo(() => {
    const parsed = deepParse(data?.PreferredContactMethods);
    if (!Array.isArray(parsed)) return [];

    return parsed.map((item: any) => ({
      label: item.Value,
      value: String(item.Key),
    }));
  }, [data]);

  const bestTimeOptions = useMemo(() => {
    const parsed = deepParse(data?.BestTimeToContact);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((item: any) => ({
      label: item.Value,
      value: String(item.Key),
    }));
  }, [data]);

  useEffect(() => {
    const availability = deepParse(data?.CustomerAvailability);
    if (!availability) return;
    const contact = preferredContactOptions.find(
      (o) => o.value === String(availability.hpi_preferredcontactmethod),
    );
    const best = bestTimeOptions.find(
      (o) => o.value === String(availability.hpi_besttimetocontact),
    );
    const availabilityDate = availability.hpi_customeravailabilitydate
      ? moment(availability.hpi_customeravailabilitydate)
      : null;
    if (contact) setContactMethod(contact);
    if (best) setBestTime(best);
    if (availabilityDate) setDate(availabilityDate);
    setId(availability?.hpi_caseextensionid);
    setInitialValues({
      contact: contact?.value,
      best: best?.value,
      date: availabilityDate ? availabilityDate.format("YYYY-MM-DD") : null,
    });
  }, [data, preferredContactOptions, bestTimeOptions]);

  const serviceWeekends = useMemo(() => {
    const parsed = deepParse(data?.HPServiceWeekend);
    if (!Array.isArray(parsed)) return [];

    return parsed.map((d: string) => d.toLowerCase());
  }, [data]);

  const handleDateChange = async (newDate: any) => {
    if (!newDate || !moment.isMoment(newDate)) return;
    // if (newDate.isSameOrBefore(moment(), "day")) {
    //   toast.error("Please select a future date");
    //   return;
    // }
    const day = newDate.format("dddd").toLowerCase();
    // if (serviceWeekends.includes(day)) {
    //   toast.error("Selected date is a holiday");
    //   return;
    // }
    setCaseExtenasionId(data?.hpi_caseextensionid);
    setDate(newDate);
    // setBestTime(null);
    dispatch(setLoading(true));
    const payload = {
      CaseId: data?.incidentid || searchParams.get("cid"),
      SelectedDate: newDate.format("YYYY-MM-DD"),
    };

    apiPost(POST_VALIDATE_DATE, payload)
      .then((response) => {
        dispatch(setLoading(false));
        if (response.data.isValid === false) {
          setDateError(response.data.message);
        } else {
          setDateError(null);
        }
      })
      .catch((error) => {
        dispatch(setLoading(false));
        toast.error("Error validating date");
      });
  };

  const handleSubmit = () => {
    const payload = {
      caseExtensionId: caseExtensionId,
      ContactMethods: contactMethod?.value,
      TimeToContact: bestTime?.value,
      CustomerAvailabilityDate: date ? moment(date).format("YYYY/MM/DD") : "",
    };
    submitAvailabilityHandler(payload);
  };

  const popupHeader = () => (
    <p className="popup-header">
      <img
        src="https://logos-world.net/wp-content/uploads/2020/11/HP-Logo.png"
        alt=""
        style={{ height: "46px", width: "auto" }}
      />
      <span>{popupTitle}</span>
    </p>
  );

  return (
    <>
      <div className="col-md-10 mb-4">
        <h4>
          {t("HPI.EditCase.Case.Customeravailabilitytitle", {
            defaultValue: "Customer Availability",
          })}

          <span className="supporting-text">
            <HelpModalPopup
              align="start"
              id="modal-popup"
              show={open}
              onClose={() => setOpen(false)}
              title={popupHeader()}
              callback={() =>
                setPopupTitle(
                  "Customer Availability for Onsite (If Applicable)",
                )
              }
            >
              <div className="col-lg-24 unWrrntyModalContent">
                <ul className="modal-list">
                  <li>
                    {t("HPI.EditCase.Customeravailabilityline1", {
                      defaultValue:
                        "This date is not final and depends on spare parts availability & eligible service for the product. The assigned field technician will confirm the final visit date",
                    })}
                  </li>
                  ,
                  <li>
                    {t("HPI.EditCase.Customeravailabilityline2", {
                      defaultValue:
                        "We may attempt to reach you via phone first, If unsuccessful, we will contact you via e-mail.",
                    })}
                  </li>
                </ul>
              </div>
            </HelpModalPopup>
          </span>
        </h4>
      </div>
      <Row className="form-row">
        <Col md={4}>
          {t("HPI.EditCase.Customeravailability.Preferredcontactmethod", {
            defaultValue: "Preferred Contact Method",
          })}
        </Col>
        <Col md={8}>
          <Select
            options={preferredContactOptions}
            value={contactMethod ? [contactMethod.value] : []}
            onChange={(option: any) => setContactMethod(option)}
            placeholder="Select"
          />
        </Col>
      </Row>

      <Row className="form-row">
        <Col md={4}>
          {t("HPI.EditCase.Customeravailability.Besttimetocontact", {
            defaultValue: "Best Time to Contact",
          })}
        </Col>
        <Col md={8}>
          <Select
            options={bestTimeOptions}
            value={bestTime ? [bestTime.value] : []}
            onChange={(option: any) => setBestTime(option)}
            placeholder="Select"
          />
        </Col>
      </Row>
      <Row className="form-row">
        <Col md={4}>
          {t("HPI.EditCase.Customeravailability.Customeravailabilitydate", {
            defaultValue: "Customer Availability",
          })}
        </Col>
        <Col md={8}>
          <DatePicker
            value={date}
            minDate={moment().add(1, "day")}
            blockPastDates
            monthYearNavigation
            placeholder="MM/DD/YYYY"
            onChange={handleDateChange}
          />
          {dateError && <div className="error-message">{dateError}</div>}{" "}
        </Col>
      </Row>
      <div className="footer mt-4">
        <Button small onClick={cancelCaseHandler}>
          {t("HPI.Cancel", { defaultValue: "Cancel" })}
        </Button>

        <Button
          small
          disabled={!!(!contactMethod || !bestTime || !date || dateError)}
          onClick={handleSubmit}
        >
          {t("HPI.EditCase.btnSubmit", { defaultValue: "Submit" })}
        </Button>
      </div>
    </>
  );
};

export default EditCustomerAvailability;
